import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";

import { ConfirmationService, MessageService as PrimeMessageService } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";
import { AdministrationCleanupService } from "@app/services/net-remit/administration.cleanup.service";

import { SingleCleanupConfigurationInfoView } from "@app/shared/models/administration/cleanup/SingleCleanupConfigurationInfoView";
import { CleanupInfoView } from "@app/shared/models/administration/cleanup/CleanupInfoView";

import { ManualCleanupFormComponent } from "@app/administration/cleanup-management/manual-cleanup-management/manual-cleanup-management-form/manual-cleanup-form.component";

import { default as constantsFile } from '@app/shared/constants.json';
import { fadeIn } from "assets/animations/animations";
import { takeWhile } from 'rxjs';

@Component({
    selector: 'app-administration-manual-cleanup-management',
    templateUrl: './manual-cleanup-management-layout.component.html',
    styleUrls: ['./manual-cleanup-management-layout.component.scss'],
    animations: [fadeIn],
    standalone: false
})

export class ManualCleanupManagementLayoutComponent {
  public alive: boolean = true;
  public isLoading: boolean = true;
  public isLoadingPeriods: boolean;
  public constants: any = constantsFile.constants;
  public allCountries: string = this.constants.AllMale;
  public manualCleanup: SingleCleanupConfigurationInfoView;
  public cleanupInfo: CleanupInfoView;

  @ViewChild(ManualCleanupFormComponent) manualCleanupComponent: ManualCleanupFormComponent;

  constructor(
    private primeMessageService: PrimeMessageService,
    private translateService: TranslateService,
    private cleanupService: AdministrationCleanupService,
    private confirmationService: ConfirmationService
  ) {
    this.manualCleanup = new SingleCleanupConfigurationInfoView(undefined, undefined, undefined, undefined);
  }

  ngOnInit() {
    this.fillCleanupInfo();
  }

  public executeManualCleanupProcess() {
    const cleanupInfo = new CleanupInfoView();
    cleanupInfo.singleCleanupConfigurationInfo = this.manualCleanup;
    const msg = this.isAnyWarning(this.manualCleanup.customerId, this.manualCleanup.periodFrom);

    if (msg !== null) {
      this.warningMessage(msg, cleanupInfo)
    } else {
      this.execute(cleanupInfo);
    }
  }

  public execute(cleanupInfo) {
    this.cleanupService.saveCleanupData(cleanupInfo)
      .pipe()
      .subscribe({
        next: (message: string) => {
          this.showToast({ isClosabled: false, severity: "success", summary: "Modal.Success", isSticky: false, detail: message });
        }, error: (error: HttpErrorResponse) => {
          this.showToast({ isClosabled: true, severity: "error", summary: "Modal.Error", isSticky: true, detail: error.error });
        }, complete: () => {
          this.isLoading = false;
        }
      });
  }

  private fillCleanupInfo(): void {
    this.cleanupService.getCleanupData()
      .pipe(takeWhile(() => this.alive))
      .subscribe({
        next: (data: CleanupInfoView) => {
          this.cleanupInfo = data;
        }, error: (error: HttpErrorResponse) => {
          this.showToast({ isClosabled: true, severity: "error", summary: "Modal.Error", isSticky: true, detail: error.error });
        }
      });
  }

  private isAnyWarning(customerId, periodFrom): string {
    if (this.cleanupInfo == null) {
      return this.translateService.instant("Administration.Cleanup.Warning.Months Retained Unknown");
    }

    const configurationInfoView = this.cleanupInfo.customerCleanupConfigurations.find(c => c.customerId === customerId);

    const months = configurationInfoView?.retentionMonths == null
      ? this.cleanupInfo.globalCleanupConfigurationInfo.retentionMonths
      : configurationInfoView?.retentionMonths;

    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - months);
    const threshold = `${currentDate.getFullYear()}${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;

    return (parseInt(threshold) <= parseInt(String(periodFrom).substring(0, 6)))
      ? this.translateService.instant("Administration.Cleanup.Warning.Months Retained Detail").replace("{{ months }}", months)
      : null;
  }

  private warningMessage(message, cleanupInfo) {
    this.confirmationService.confirm({
      message: message,
      header: this.translateService.instant("Administration.Cleanup.Warning.Months Retained Title"),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      accept: () => {
        this.execute(cleanupInfo);
      },
      reject:() =>{
        this.manualCleanupComponent.clean();
      }
    })
  }

  public isLoadingMethod(value: boolean){
    this.isLoading = value;
  }

  public isLoadingPeriodsMethod(value: boolean){
    this.isLoadingPeriods = value;
  }

  public showToast(data: { isClosabled: boolean; severity: string; summary: string; isSticky: boolean, detail: string }) {
    this.primeMessageService.add({
      closable: data.isClosabled,
      sticky: data.isSticky,
      severity: data.severity,
      summary: this.translateService.instant(data.summary),
      detail: this.translateService.instant(data.detail)
    });
  }
}
